// ============================================================
// POPUP DESIGN MODEL — the single design document (2026 schema)
// ============================================================
//
// Stored inside popup_settings.style_settings under the `design` key, the
// only key a row holds since the 8 Oct 2026 migration (the pre-2026
// `welcome` / `success` colour blocks were folded into it). There is exactly
// ONE renderer: normalizeDesign() maps the stored document onto base defaults
// that reproduce the pre-2026 look (white panel, Inter font, teal focus
// rings, centered modal).
//
// Rules of the schema:
// - Every field in the *Input types is optional; the normalizer fills gaps.
// - Unknown keys in style_settings are preserved by the builder (never strip
//   what we don't understand — live rows carry stray keys like
//   content_settings.welcome.template).

// ---------- primitives ----------

export type PopupFormat = "modal" | "fullscreen" | "slidein" | "bar" | "embedded";

/** Placement of the popup itself. Only some combinations are valid per format:
 *  modal/fullscreen → center; slidein → bottom_right/bottom_left/top_right/top_left;
 *  bar → top/bottom; embedded → ignored. */
export type PopupPosition =
  | "center"
  | "top"
  | "bottom"
  | "bottom_right"
  | "bottom_left"
  | "top_right"
  | "top_left";

export type PopupSizePreset = "sm" | "md" | "lg" | "custom";

export type BackgroundType = "solid" | "gradient" | "image";

export type EntryAnimation = "fade" | "slide_up" | "zoom" | "none";

export type ShadowPreset = "none" | "sm" | "md" | "lg" | "glow";

export type PaddingPreset = "compact" | "comfortable" | "spacious";

export type TextSizePreset = "sm" | "md" | "lg" | "xl";

/** `background` renders the image as the popup's full-bleed backdrop. */
export type ImagePlacement = "left" | "right" | "top" | "bottom" | "background";

export type ImageFit = "cover" | "contain";

/** `flush` = legacy edge-to-edge (touches the popup's corners);
 *  `card` = inset inside the panel with its own corner radius. */
export type ImageStyle = "flush" | "card";

export type InputSizePreset = "sm" | "md" | "lg" | "xl";

export type TeaserMode = "off" | "after_close" | "teaser_first";

/** What a teaser click DOES. "popup" is the historical behaviour and the
 *  default; "rewards" opens the visitor's Passes member dashboard when their
 *  pass serial is known from a previous signup, else falls back to the popup. */
export type TeaserAction = "popup" | "whatsapp" | "url" | "rewards";

/** What happens to the teaser AFTER the visitor submits. "hide" is the
 *  historical behaviour (converted visitors get neither popup nor teaser);
 *  "reward" keeps the tab alive as a way back to their code/pass. */
export type TeaserPostSignup = "hide" | "reward";

export type TeaserPosition = "left" | "right" | "bottom_left" | "bottom_right";

export type TeaserOrientation = "vertical" | "horizontal";

// ---------- theme ----------

export interface DesignGradient {
  from: string;
  to: string;
  /** degrees, CSS linear-gradient angle */
  angle: number;
}

export interface DesignBackgroundImage {
  url: string;
  /** Optional phone-specific backdrop (viewports < 768px) — a portrait
   *  composition instead of the desktop landscape one cover-cropped to a
   *  phone. ""/absent → `url` everywhere. Same overlay applies. */
  mobileUrl?: string;
  /** Phone EDGE ART (<768px): transparent PNG strips pinned to the top and
   *  bottom of the visible screen at full width, layered over `mobileUrl`
   *  (or `url`). This is how a "floating products" backdrop survives every
   *  phone height: a single baked picture is cover-cropped to the screen's
   *  proportions and loses its bottom row behind the browser toolbar, while
   *  edge strips always hug the visible edges (the Alia/Retropia layout pins
   *  its product images to the container edges). Either may be empty. */
  mobileTopUrl?: string;
  mobileBottomUrl?: string;
  /** overlay drawn over the image so text stays legible */
  overlayColor: string;
  /** 0..1 */
  overlayOpacity: number;
}

export interface DesignBackground {
  type: BackgroundType;
  /** used when type === "solid" (and as fallback while an image loads) */
  color: string;
  gradient?: DesignGradient;
  image?: DesignBackgroundImage;
}

export interface DesignOverlay {
  /** full CSS color (rgba fine) — legacy rows store a combined rgba here */
  color: string;
  /** backdrop blur in px, 0 = none */
  blur: number;
}

export interface DesignAnimation {
  entry: EntryAnimation;
  durationMs: number;
}

export interface DesignTheme {
  /** Font family name from the curated list (e.g. "Inter", "Playfair Display").
   *  Loaded via a namespaced <link> in the host document. */
  fontFamily: string;
  /** Floating presentation: the step renders with NO panel chrome — no
   *  background, shadow or clipping — its elements sit directly over the
   *  overlay (the SVYP deck look). Modal/fullscreen formats only; resolves
   *  per step via steps overrides. Default false. */
  frameless?: boolean;
  background: DesignBackground;
  /** CSS length, e.g. "16px" */
  borderRadius: string;
  shadow: ShadowPreset;
  padding: PaddingPreset;
  overlay: DesignOverlay;
  animation: DesignAnimation;
}

// ---------- per-element styling ----------

export type TextAlign = "left" | "center" | "right";

/** Vertical spacing the designer controls (8 Oct 2026). Unset = the layout's
 *  built-in gap, so a popup that never sets these renders exactly as before.
 *  0 is a real value (no gap). Rendered by `spaceAboveProps` (class + var +
 *  !important, because the widget shell forces the space-y utilities). */
export interface SpaceAbove {
  /** Exact space above this element in px (desktop, and phones unless
   *  mobileSpaceAbovePx is set). Unset / null = the layout's default gap. */
  spaceAbovePx?: number | null;
  /** Exact space above on phones (<768px). Unset / null = spaceAbovePx, else
   *  the layout's default gap. */
  mobileSpaceAbovePx?: number | null;
}

export interface TextElementStyle extends SpaceAbove {
  color: string;
  size: TextSizePreset;
  /** Exact desktop px that OVERRIDES the size preset when > 0 (still scales
   *  down responsively via the same clamp() strategy as the presets). */
  sizePx?: number;
  /** Exact phone px (<768px) when > 0 — beats the responsive clamp on
   *  phones, so mobile type can be BIGGER than desktop (the Alia/Retropia
   *  mobile layout) instead of only ever scaling down. 0/absent = clamp. */
  mobileSizePx?: number;
  /** CSS font-weight */
  weight: number;
  /** default "center" — the legacy renderer centered all copy */
  align: TextAlign;
  /** Phone-only text-align override (<768px). ""/undefined = inherit `align`.
   *  Applied via a media-scoped class (inline text-align carries desktop). */
  mobileAlign?: "" | TextAlign;
  italic: boolean;
  /** Per-element font family from POPUP_FONTS. ""/undefined → inherit
   *  theme.fontFamily (an inherited serif heading over a sans body, etc.). */
  fontFamily?: string;
  /** Unitless line-height override; undefined → the per-kind default. */
  lineHeight?: number;
  /** CSS letter-spacing (e.g. "0.05em"); ""/undefined → normal. */
  letterSpacing?: string;
}

export interface InputsStyle extends SpaceAbove {
  /** Exact space between the form fields in px (desktop, and phones unless
   *  mobileGapPx is set). Unset / null = the layout's default (12 px beside
   *  an image, 20 / 24 px without). Rendered by `fieldGapProps`. */
  gapPx?: number | null;
  /** Space between the form fields on phones (<768px). Unset / null = gapPx,
   *  else the layout's default. */
  mobileGapPx?: number | null;
  background: string;
  textColor: string;
  placeholderColor: string;
  borderColor: string;
  focusBorderColor: string;
  /** CSS length */
  radius: string;
  /** Border width in px. 2 = the legacy hardcoded border-2 look. */
  borderWidth: number;
  /** Field height scale. "md" = legacy h-11/h-12. */
  size: InputSizePreset;
  /** Drag-resize: shared width of ALL fields, % of the form column (desktop,
   *  ≥768px). 0 = auto (legacy full-width). Set by the builder's handles. */
  customWidthPct: number;
  /** Drag-resize width on phones (<768px). 0 = auto (full width). */
  customMobileWidthPct: number;
  /** Drag-resize: exact field height in px (desktop). 0 = auto (the `size`
   *  scale). Vertical drag on a field's corner handle writes this. */
  customHeightPx: number;
  /** Drag-resize field height on phones. 0 = auto. */
  customMobileHeightPx: number;
  /** Horizontal placement when narrower than the column (desktop, and the
   *  phone fallback when `mobileAlign` is unset). */
  align: "left" | "center" | "right";
  /** Phone-only alignment override (<768px). "" = inherit `align`. */
  mobileAlign: "" | "left" | "center" | "right";
}

export interface ButtonStyle extends SpaceAbove {
  background: string;
  textColor: string;
  /** undefined → keep background on hover (legacy behaviour is a scale effect) */
  hoverBackground?: string;
  /** "lit" (15 Oct 2026, the resources.flowapt.com sign-in button): lit from
   *  above. A top-to-bottom gradient made from `background`, four layered
   *  shadows (top highlight, bottom shade, contact shadow, own-colour glow)
   *  that replace `shadow`, a lighter gradient on hover (from
   *  `hoverBackground` when set) and a pressed state. Needs a hex or rgb()
   *  background; anything else renders flat. Absent / "flat" = legacy. */
  finish?: "flat" | "lit";
  /** CSS length */
  radius: string;
  size: "sm" | "md" | "lg";
  shadow: ShadowPreset;
  /** CSS letter-spacing for the label (e.g. "0.08em"); ""/undefined → normal. */
  letterSpacing?: string;
  /** Exact label font-size in px (desktop, ≥768px). 0 = auto (the `size`
   *  scale's responsive text classes). */
  fontSizePx: number;
  /** Exact label font-size in px on phones (<768px). 0 = auto. */
  mobileFontSizePx: number;
  /** Drag-resize width, % of the form column (desktop). 0 = auto (full). */
  customWidthPct: number;
  /** Drag-resize width on phones. 0 = auto (full width). */
  customMobileWidthPct: number;
  /** Drag-resize exact height in px (desktop). 0 = auto (the `size` scale). */
  customHeightPx: number;
  /** Drag-resize height on phones. 0 = auto. */
  customMobileHeightPx: number;
  /** Horizontal placement when narrower than the column (desktop, and the
   *  phone fallback when `mobileAlign` is unset). */
  align: "left" | "center" | "right";
  /** Phone-only alignment override (<768px). "" = inherit `align`. */
  mobileAlign: "" | "left" | "center" | "right";
}

export interface IconsStyle {
  /** WhatsApp icon + placeholder art accent */
  accentColor: string;
}

export interface CloseButtonStyle {
  color: string;
  background: string;
  /** Where the ✕ lives: "panel" (legacy — the panel's corner) or "screen"
   *  (pinned to the viewport's top-right, outside the panel — the SVYP
   *  pattern). Modal/fullscreen only; other formats always use "panel". */
  placement?: "panel" | "screen";
}

/** The thank-you page's discount code box (label + dashed code box). Its
 *  colour, weight and font follow the message (`body`); only what a
 *  designer moves on its own lives here. "" = follow the message's
 *  alignment; 0 = the message's size. (8 Oct 2026) */
export interface CodeBoxStyle {
  align: "" | TextAlign;
  mobileAlign: "" | TextAlign;
  /** Label font size in px; 0 = the message's size. */
  sizePx: number;
  mobileSizePx: number;
}

export interface DesignElements {
  heading: TextElementStyle;
  subheading: TextElementStyle;
  /** success message / body copy */
  body: TextElementStyle;
  footer: TextElementStyle;
  inputs: InputsStyle;
  button: ButtonStyle;
  icons: IconsStyle;
  closeButton: CloseButtonStyle;
  /** The discount code box on the thank-you page. */
  code: CodeBoxStyle;
}

// ---------- image ----------

export interface DesignImage {
  enabled: boolean;
  url: string;
  /** Optional phone-specific image (viewports < 768px) — e.g. a landscape
   *  crop for the stacked mobile layout. "" → `url` everywhere. Applies to
   *  layout images (left/right/top/bottom), not position "background". */
  mobileUrl: string;
  /** Hide the layout image entirely on phones (<768px) — the stacked mobile
   *  layout can get heavy with an image + full form. Desktop unaffected;
   *  ignored for position "background". */
  hideOnMobile: boolean;
  alt: string;
  position: ImagePlacement;
  fit: ImageFit;
  /** % of popup width the image column takes in side layouts (desktop) */
  widthPct: number;
  /** Explicit drag-resize width, % of the popup row (desktop, ≥768px).
   *  0 = auto (the legacy per-style sizing — natural aspect capped at 55%,
   *  widthPct for contain+flush, etc). Written by the builder's on-canvas
   *  drag handles; when > 0 it overrides every style-specific auto width. */
  customWidthPct: number;
  /** Explicit drag-resize width on phones (<768px), % of the popup width,
   *  centered. 0 = auto (legacy full-width stacked image). */
  customMobileWidthPct: number;
  /** "flush" (legacy, default) or "card" — inset with its own radius.
   *  Ignored for position "background" and the bar format. */
  style: ImageStyle;
  /** card only: CSS length gutter between the image and the popup edges */
  inset: string;
  /** card only: CSS length corner radius on the image itself */
  radius: string;
}

// ---------- dismiss link ("No thanks" under the CTA) ----------

/** Optional text link under the submit button that CLOSES the popup — the
 *  Klaviyo-style decline ("No thanks, I'll pay full price") for visitors who
 *  miss the ✕. Welcome step only (the success step's button already closes);
 *  not rendered in the bar format (no vertical room). */
export interface DesignDismiss {
  enabled: boolean;
  text: string;
  /** Presentation: subtle text link (default) or a full-width secondary
   *  BUTTON styled like the CTA. */
  style: "link" | "button";
  /** Text colour. "" = auto (link: muted gray #6b7280; button: #374151). */
  color: string;
  /** Button mode only: fill colour. "" = light gray #f3f4f6. */
  background: string;
  /** Button mode only: CSS corner radius. "" = inherit the main button's. */
  radius: string;
  /** Link mode only: underlined by default — reads as a link. */
  underline: boolean;
  /** Exact font-size in px. 0 = default (link 13px, button 14px). */
  fontSizePx: number;
}

// ---------- teaser (edge-of-screen card) ----------

export interface DesignTeaser {
  enabled: boolean;
  mode: TeaserMode;
  /** e.g. "Get R100 off" */
  text: string;
  position: TeaserPosition;
  orientation: TeaserOrientation;
  background: string;
  textColor: string;
  /** CSS length */
  radius: string;
  /** show the WhatsApp icon on the card */
  showIcon: boolean;
  /** Click behaviour. Absent/"popup" = open the popup (legacy). */
  action: TeaserAction;
  /** action "whatsapp": wa.me number (digits). "" = action falls back to popup. */
  actionNumber: string;
  /** action "whatsapp": optional pre-filled message. */
  actionMessage: string;
  /** action "url": destination, opened in a new tab. "" = falls back to popup. */
  actionUrl: string;
  /** After-signup behaviour. Absent/"hide" = legacy suppression. */
  postSignup: TeaserPostSignup;
  /** Tab label in the post-signup reward state. "" = "Your reward". */
  postSignupText: string;
}

// ---------- size ----------

export interface DesignSize {
  preset: PopupSizePreset;
  /** only read when preset === "custom" — raw CSS lengths, run through the
   *  same responsive clamps the legacy renderer used */
  width?: string;
  maxWidth?: string;
  minWidth?: string;
  /** Max width of the INNER content column in px (10 Aug 2026). 0/absent =
   *  the legacy 360px cap (FORM_MAX_WIDTH). This — not theme.padding — is
   *  what governs the side white space on popups wider than ~400px: the
   *  column is centered, so padding changes were invisible while the column
   *  cap dominated. Always rendered as min(100%, Npx) so phones stay safe. */
  contentMaxWidth?: number;
}

// ---------- text image ----------

/** Designer-supplied artwork that REPLACES the heading + description text
 *  block for a step (the copy is baked into the image — e.g. a serif-italic
 *  flourish no live font can reproduce). Lives PER STEP only (never on the
 *  design root): the artwork carries step-specific copy, so it must never
 *  inherit from welcome onto success. "" url → live text renders as normal. */
export interface DesignTextImage {
  url: string;
  /** Optional phone-specific version (viewports < 768px). "" → `url`. */
  mobileUrl: string;
  /** Drag-resize width, % of the content column (desktop, ≥768px).
   *  0 = auto (legacy full-width). Written by the builder's drag handles. */
  widthPct: number;
  /** Drag-resize width on phones (<768px), % of the content column.
   *  0 = auto (legacy full-width). */
  mobileWidthPct: number;
  /** Horizontal placement when narrower than the column (desktop, and the
   *  phone fallback when `mobileAlign` is unset). */
  align: "left" | "center" | "right";
  /** Phone-only alignment override (<768px). "" = inherit `align`. */
  mobileAlign: "" | "left" | "center" | "right";
}

// ---------- per-step overrides ----------

/** Deep-partial overrides applied on top of the base design for one step. */
export interface StepDesignOverrides {
  theme?: DeepPartial<DesignTheme>;
  elements?: DeepPartial<DesignElements>;
  image?: Partial<DesignImage>;
  textImage?: Partial<DesignTextImage>;
  /** Per-step panel sizing (e.g. a narrow success card after a wide gate). */
  size?: Partial<DesignSize>;
}

// ---------- custom fonts ----------

export interface ContentPanelStyle {
  enabled: boolean;
  background: string;
  radius: string;
  padding: string;
  maxWidth: string;
  align: "left" | "center" | "right";
  /** Horizontal distance from the screen edge (CSS length / %). */
  inset: string;
  phones: "panel" | "plain";
}

export interface CustomFontFace {
  /** CSS font-weight this file provides (300/400/500/600/700…) */
  weight: number;
  style: "normal" | "italic";
  /** PUBLIC, non-expiring URL of the font file (otf/ttf/woff/woff2) —
   *  popup_fonts Storage bucket. Signed URLs would silently break live
   *  popups when they expire. */
  url: string;
}

/** An org-uploaded font family (e.g. the client's licensed brand font).
 *  Rides inside the design document so the widget needs no extra fetches;
 *  any fontFamily in the design matching `family` loads via @font-face
 *  instead of Google Fonts. */
export interface CustomFont {
  family: string;
  faces: CustomFontFace[];
}

// ---------- the document ----------

/** Fully-resolved design — what the renderer consumes. Every field present. */
export interface PopupDesign {
  format: PopupFormat;
  /** Phones (<768px) only: "fullscreen" turns a centred modal into an
   *  edge-to-edge sheet — the Alia mobile layout (every showcase popup is a
   *  full-screen sheet on a phone and a centred panel on desktop). Absent /
   *  "inherit" = same format everywhere. Only affects format "modal". */
  mobileFormat?: "inherit" | "fullscreen" | "card";
  /** Phones + mobileFormat "card": the margin between the screen edge and
   *  the full-height card (the overlay shows in it). CSS length, default 5vw. */
  mobileCardInset?: string;
  /** Desktop: the modal fills the screen as an inset card (full height,
   *  overlay framing it, image columns run the card's height) — Alia's
   *  Aether desktop layout. Phones follow mobileFormat. */
  cardFill?: boolean;
  /** Full-screen pages only: draw the content (copy, form, game) inside a
   *  styled box placed left / centre / right over the backdrop — Alia's
   *  "Opal" desktop layout (IM8's red panel on the right of the photo).
   *  `phones` decides whether the box also shows under 768px. */
  contentPanel?: ContentPanelStyle;
  /** Desktop margin for cardFill. CSS length, default 16px. */
  cardInset?: string;
  position: PopupPosition;
  size: DesignSize;
  theme: DesignTheme;
  elements: DesignElements;
  image: DesignImage;
  teaser: DesignTeaser;
  /** Optional "No thanks" close link under the CTA (welcome step). */
  dismiss: DesignDismiss;
  /** Hide the top-right ✕ so the dismiss element is the only visible close.
   *  GUARDED: only honoured when another close path exists (dismiss enabled
   *  or overlayClose) — a popup can never become unclosable. Escape still
   *  works regardless (accessibility). */
  hideCloseButton: boolean;
  /** Clicking the dim overlay closes the popup. Defaults FALSE — the legacy
   *  renderer never did this, so pre-2026 popups must not change behaviour. */
  overlayClose: boolean;
  /** Uploaded font families available to this popup (absent on most rows). */
  customFonts?: CustomFont[];
  steps: {
    welcome?: StepDesignOverrides;
    success?: StepDesignOverrides;
    /** Deck-step tweaks applied ON TOP of the resolved welcome design (the
     *  deck wears the welcome skin by default). */
    deck?: StepDesignOverrides;
    /** Scratch-step tweaks, same inheritance as the deck's. */
    scratch?: StepDesignOverrides;
    /** Wheel-step tweaks, same inheritance as the deck's. */
    wheel?: StepDesignOverrides;
    /** welcome + tweaks for the question pages (e.g. their own photo). */
    question?: StepDesignOverrides;
    /** welcome + tweaks for the phone page (e.g. its own photo). */
    phone?: StepDesignOverrides;
  };
}

/** What's stored under style_settings.design — everything optional. */
export type PopupDesignInput = DeepPartial<PopupDesign>;

/** A step's design after base + step overrides are merged. */
export interface ResolvedStepDesign {
  format: PopupFormat;
  position: PopupPosition;
  size: DesignSize;
  theme: DesignTheme;
  elements: DesignElements;
  image: DesignImage;
  textImage: DesignTextImage;
}

export interface NormalizedPopupDesign {
  design: PopupDesign;
  welcome: ResolvedStepDesign;
  success: ResolvedStepDesign;
  /** welcome + steps.deck overrides — what the deck step actually wears. */
  deck: ResolvedStepDesign;
  /** welcome + steps.scratch overrides — what the scratch step wears. */
  scratch: ResolvedStepDesign;
  /** welcome + steps.wheel overrides — what the spin-to-win step wears. */
  wheel: ResolvedStepDesign;
  /** welcome + steps.question overrides — what the question pages wear. */
  question: ResolvedStepDesign;
  /** welcome + steps.phone overrides — what the phone page wears. */
  phone: ResolvedStepDesign;
}

// ---------- fonts ----------

/** Curated font list. Key = family name (also the Google Fonts family). */
export const POPUP_FONTS: { family: string; category: "sans" | "serif" | "display" | "script" | "mono" }[] = [
  // sans
  { family: "Inter", category: "sans" },
  { family: "Roboto", category: "sans" },
  { family: "Open Sans", category: "sans" },
  { family: "Lato", category: "sans" },
  { family: "Poppins", category: "sans" },
  { family: "Montserrat", category: "sans" },
  { family: "DM Sans", category: "sans" },
  { family: "Manrope", category: "sans" },
  { family: "Nunito", category: "sans" },
  { family: "Raleway", category: "sans" },
  { family: "Work Sans", category: "sans" },
  { family: "Rubik", category: "sans" },
  { family: "Karla", category: "sans" },
  { family: "Outfit", category: "sans" },
  { family: "Sora", category: "sans" },
  { family: "Plus Jakarta Sans", category: "sans" },
  { family: "Figtree", category: "sans" },
  { family: "Lexend", category: "sans" },
  { family: "Mulish", category: "sans" },
  { family: "Barlow", category: "sans" },
  { family: "Cabin", category: "sans" },
  { family: "Josefin Sans", category: "sans" },
  { family: "Quicksand", category: "sans" },
  { family: "Archivo", category: "sans" },
  { family: "Space Grotesk", category: "sans" },
  { family: "Urbanist", category: "sans" },
  { family: "Source Sans 3", category: "sans" },
  { family: "PT Sans", category: "sans" },
  // serif
  { family: "Playfair Display", category: "serif" },
  { family: "Cormorant Garamond", category: "serif" },
  { family: "Lora", category: "serif" },
  { family: "Libre Baskerville", category: "serif" },
  { family: "Merriweather", category: "serif" },
  { family: "EB Garamond", category: "serif" },
  { family: "Crimson Text", category: "serif" },
  { family: "DM Serif Display", category: "serif" },
  // display
  { family: "Bebas Neue", category: "display" },
  { family: "Abril Fatface", category: "display" },
  { family: "Anton", category: "display" },
  { family: "Righteous", category: "display" },
  { family: "Oswald", category: "display" },
  // script
  { family: "Caveat", category: "script" },
  { family: "Pacifico", category: "script" },
  { family: "Dancing Script", category: "script" },
  // mono
  { family: "Space Mono", category: "mono" },
  { family: "JetBrains Mono", category: "mono" },
];

export const FONT_FALLBACK_STACK =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";

// ---------- utility type ----------

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? U[]
    : T[P] extends object
      ? DeepPartial<T[P]>
      : T[P];
};
